import { useEffect, useState, type ReactNode } from 'react';

/**
 * A card that folds away, remembering whether it was open.
 *
 * The state is per device rather than per account: which panels you keep open
 * depends on the screen in front of you, and a teacher on a laptop shouldn't
 * inherit the choices they made on a projector.
 */
export function CollapsiblePanel({
  title,
  /** Stable key for remembering the open state. */
  storageKey,
  badge,
  defaultOpen = true,
  className = '',
  children,
  ...rest
}: {
  title: string;
  storageKey: string;
  badge?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, 'title' | 'children'>) {
  const key = `td.panel.${storageKey}`;
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? defaultOpen : raw === 'open';
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, open ? 'open' : 'closed');
    } catch {
      // Storage blocked — folding still works, it just won't be remembered.
    }
  }, [key, open]);

  return (
    <section className={['card p-3', className].join(' ')} {...rest}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
      >
        <span
          aria-hidden
          className={['text-xs text-stone transition-transform', open ? 'rotate-90' : ''].join(' ')}
        >
          ▶
        </span>
        <span className="flex-1 text-sm text-espresso">{title}</span>
        {badge != null && <span className="text-xs text-stone">{badge}</span>}
      </button>

      {open && <div className="mt-3">{children}</div>}
    </section>
  );
}
